import { useState, useRef, useCallback, useEffect, useId } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import {
  Plus,
  Trash2,
  Music,
  Volume2,
  Upload,
  Loader2,
  Pencil,
  Check,
  X,
  Play,
  Square,
  FileAudio,
} from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { cn } from "@/client/lib/utils";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/client/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/client/components/ui/alert-dialog";
import {
  collections,
  createAudioAsset,
  uploadAudioFile,
  updateAudioAsset,
  deleteAudioAsset,
  type AudioAsset,
  type AudioAssetType,
} from "@/client/db";

interface AudioAssetsPanelProps {
  storyId: string;
  maxHeight?: string;
}

export function AudioAssetsPanel({
  storyId,
  maxHeight = "h-[400px]",
}: AudioAssetsPanelProps) {
  const formId = useId();
  const { data: allAssets } = useLiveQuery(() => collections().audioAssets.collection, [storyId]);
  const assets = (allAssets ?? []).filter((a: AudioAsset) => a.storyId === storyId);
  
  const musicAssets = assets.filter((a) => a.type === "music");
  const sfxAssets = assets.filter((a) => a.type === "sfx");

  const [isCreating, setIsCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAssetName, setNewAssetName] = useState("");
  const [newAssetFilename, setNewAssetFilename] = useState("");
  const [newAssetType, setNewAssetType] = useState<AudioAssetType>("music");
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AudioAsset | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string | null>(null);

  // Plays here, one at a time, rather than opening the file in a new tab
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const togglePlay = useCallback(
    (assetId: string) => {
      audioRef.current?.pause();
      if (playingId === assetId) {
        setPlayingId(null);
        return;
      }
      const audio = new Audio(`/api/audio-assets/${assetId}/audio`);
      audioRef.current = audio;
      audio.onended = () => setPlayingId((current) => (current === assetId ? null : current));
      audio.onerror = () => setPlayingId((current) => (current === assetId ? null : current));
      setPlayingId(assetId);
      void audio.play().catch(() => setPlayingId(null));
    },
    [playingId]
  );
  useEffect(() => () => audioRef.current?.pause(), []);

  const handleCreate = useCallback(async () => {
    if (!newAssetName.trim() || !newAssetFilename.trim()) return;
    
    setIsCreating(true);
    try {
      await createAudioAsset(storyId, newAssetName.trim(), newAssetFilename.trim(), newAssetType);
      setNewAssetName("");
      setNewAssetFilename("");
      setNewAssetType("music");
      setCreateDialogOpen(false);
    } finally {
      setIsCreating(false);
    }
  }, [storyId, newAssetName, newAssetFilename, newAssetType]);

  const handleStartUpload = useCallback((assetId: string) => {
    uploadTargetRef.current = assetId;
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const assetId = uploadTargetRef.current;
    if (!file || !assetId) return;

    setUploadingId(assetId);
    try {
      await uploadAudioFile(assetId, file);
    } finally {
      setUploadingId(null);
      uploadTargetRef.current = null;
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, []);

  const handleStartEdit = useCallback((asset: AudioAsset) => {
    setEditingId(asset.id);
    setEditingName(asset.name);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId || !editingName.trim()) return;
    await updateAudioAsset(editingId, { name: editingName.trim() });
    setEditingId(null);
    setEditingName("");
  }, [editingId, editingName]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingName("");
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteAudioAsset(deleteTarget.id);
    setDeleteTarget(null);
  }, [deleteTarget]);

  const renderAssetItem = (asset: AudioAsset) => {
    const Icon = asset.type === "music" ? Music : Volume2;
    const isEditing = editingId === asset.id;
    const isUploading = uploadingId === asset.id;

    return (
      <div
        key={asset.id}
        className="flex items-center gap-2 p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors group overflow-hidden"
      >
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />

        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit();
                  if (e.key === "Escape") handleCancelEdit();
                }}
                className="h-7 text-sm"
                autoFocus
              />
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSaveEdit}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleCancelEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{asset.name}</div>
              <div className="text-xs text-muted-foreground truncate">{asset.filename}</div>
            </div>
          )}
        </div>

        {/* Status indicator - hide on hover when showing actions */}
        <div className="shrink-0 group-hover:hidden">
          {asset.hasAudioData ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-600">
              Uploaded
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-600">
              Reference
            </span>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div
            className={cn(
              "hidden group-hover:flex group-focus-within:flex [@media(hover:none)]:flex items-center gap-1 shrink-0",
              playingId === asset.id && "flex"
            )}
          >
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => handleStartUpload(asset.id)}
              disabled={isUploading}
              title="Upload audio file"
            >
              {isUploading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Upload className="h-3 w-3" />
              )}
            </Button>
            {asset.hasAudioData && (
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => togglePlay(asset.id)}
                title={playingId === asset.id ? "Stop" : "Play"}
                aria-label={playingId === asset.id ? `Stop ${asset.name}` : `Play ${asset.name}`}
              >
                {playingId === asset.id ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={() => handleStartEdit(asset)}
              title="Rename"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget(asset)}
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2 overflow-hidden">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/ogg,audio/mpeg,audio/wav,audio/mp3"
        className="hidden"
        onChange={handleFileSelect}
      />

      <ScrollArea className={maxHeight}>
        {assets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center border-2 border-dashed rounded-lg">
            <FileAudio className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground mb-2">No audio assets yet</p>
            <p className="text-xs text-muted-foreground/70 mb-4">
              Add music and sound effects for your scenes
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Music section */}
            {musicAssets.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Music className="h-3 w-3" />
                  Music ({musicAssets.length})
                </h4>
                <div className="space-y-2">
                  {musicAssets.map(renderAssetItem)}
                </div>
              </div>
            )}

            {/* SFX section */}
            {sfxAssets.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Volume2 className="h-3 w-3" />
                  Sound Effects ({sfxAssets.length})
                </h4>
                <div className="space-y-2">
                  {sfxAssets.map(renderAssetItem)}
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Add button */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Audio
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Audio Asset</DialogTitle>
            <DialogDescription>
              Create a reference to an audio file. You can upload the actual file later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor={`${formId}-name`}>Display Name</Label>
              <Input
                id={`${formId}-name`}
                value={newAssetName}
                onChange={(e) => setNewAssetName(e.target.value)}
                placeholder="Happy Theme"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`${formId}-filename`}>File Name</Label>
              <Input
                id={`${formId}-filename`}
                value={newAssetFilename}
                onChange={(e) => setNewAssetFilename(e.target.value)}
                placeholder="bgm_happy.ogg"
              />
              <p className="text-xs text-muted-foreground">
                The file's name in the Ren'Py game, with its extension
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`${formId}-type`}>Type</Label>
              <Select value={newAssetType} onValueChange={(v) => setNewAssetType(v as AudioAssetType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="music">
                    <span className="flex items-center gap-2">
                      <Music className="h-4 w-4" />
                      Music
                    </span>
                  </SelectItem>
                  <SelectItem value="sfx">
                    <span className="flex items-center gap-2">
                      <Volume2 className="h-4 w-4" />
                      Sound Effect
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !newAssetName.trim() || !newAssetFilename.trim()}>
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audio Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
